/**
 * v3 main onboarding motion — stagger + dock + `authFormSwap`. Self-contained (do not import v1 motion).
 */
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { Platform } from 'react-native';
import {
  Easing,
  interpolate,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import type { AuthSheetState } from '../../auth/authSheetContext';
import type { OverlayPanelThemeColors } from '../../../components/common/ConfigurableOverlay';
import { overlayPanelTransform } from '../../../components/common/ConfigurableOverlay';
import { MAIN_SCREEN_DOCK } from '../../auth/onboardingMainScreenConfig';
import { stripAndroidMainScreenOverlayPanelFill } from '../../auth/mainScreenOverlayPanel';
import { isMainScreenAndroid } from '../../auth/onboardingMainScreenAndroidParity';
import { isWebRuntime } from '../../platform/runtimeTarget';

export const ONBOARD_TRANSITION_MS = 240;
/** Linear so staggered / looping onboarding motion does not drift at segment boundaries. */
export const ONBOARD_EASING = Easing.linear;

/** Centered main-screen auth float: same slide offset as `MAIN_SCREEN_MOTION` in `authOverlayPresentation`. */
export const MAIN_SCREEN_DOCK_SLIDE_OFF = 0;
/** Scale preset vertical travel — matches v1 `MAIN_SCREEN_AUTH_PANEL_VH_FRAC`. */
export const MAIN_SCREEN_AUTH_PANEL_VH_FRAC = 0.085;
/** Open/close duration (ms) for the floating auth card — matches previous main-screen overlay. */
export const MAIN_SCREEN_DOCK_MS = 240;

export type SheetPhase = 'idle' | 'login' | 'create';

const STAGGER = 28;
const IS_ANDROID_MOTION = Platform.OS === 'android';
/** Scale-to-0 on chip label/chevron breaks crossfade compositing on Android. */
const CHIP_PART_OPACITY_ONLY = IS_ANDROID_MOTION;
/** Hero card shell — same dim as iOS on both platforms so the green card stays visible behind the form. */
const CARD_DOCK_OPACITY: [number, number, number] = [1, 0.93, 0.93];
/** Cluster rows / auth chips when dock open (iOS baseline — hero content shifts up inside the card). */
const DOCK_CHROME_OPACITY: [number, number, number] = [1, 0.93, 0.93];
const DOCK_CLUSTER_OPACITY: [number, number, number] = [1, 0.4, 0];
const DOCK_JOURNEY_OPACITY: [number, number, number] = [1, 0.6, 0];
const DOCK_CONSULT_OPACITY: [number, number, number] = [1, 0.9, 0];
const DOCK_LANG_OPACITY: [number, number, number] = [1, 1, 0];
const DOCK_CLUSTER_Y: [number, number, number] = [0, -12, -12];
const DOCK_JOURNEY_Y: [number, number, number] = [0, -18, -18];
const DOCK_CONSULT_Y: [number, number, number] = [0, -22, -22];
const DOCK_LOGO_Y: [number, number, number] = [0, -4, -14];
const DOCK_LOGO_SCALE: [number, number, number] = [1, 1, 0.72];

export function useSheetPhase(auth_state: AuthSheetState): SheetPhase {
  if (auth_state?.kind === 'login') return 'login';
  if (auth_state?.kind === 'register') return 'create';
  return 'idle';
}

function useChipPartStyle(pPatt: SharedValue<number>, opacity: [number, number, number], scale: [number, number, number]) {
  return useAnimatedStyle(() => {
    'worklet';
    return {
      opacity: interpolate(pPatt.value, [0, 1, 2], opacity),
      transform: CHIP_PART_OPACITY_ONLY
        ? [{ scale: 1 }]
        : [{ scale: interpolate(pPatt.value, [0, 1, 2], scale) }],
    };
  });
}

/** Menu / journey / consult: opacity + translateY (vh) + scale over three phase stops. */
function useClusterRowStyle(
  p: SharedValue<number>,
  wh: SharedValue<number>,
  opacity: [number, number, number],
  yMul: [number, number, number],
  scale: [number, number, number],
) {
  return useAnimatedStyle(() => {
    'worklet';
    const w = wh.value;
    const ty = (m: number) => (w * m) / 100;
    return {
      opacity: interpolate(p.value, [0, 1, 2], opacity),
      transform: [
        { translateY: interpolate(p.value, [0, 1, 2], [ty(yMul[0]), ty(yMul[1]), ty(yMul[2])]) },
        { scale: interpolate(p.value, [0, 1, 2], scale) },
      ],
    };
  });
}
function useBlurOverlayStyle(p: SharedValue<number>) {
  return useAnimatedStyle(() => {
    'worklet';
    return {
      // Blur is "off" at phase 0, and fully "on" at phase 1 & 2
      opacity: interpolate(p.value, [0, 1, 2], [0, 1, 1]),
    };
  });
}
export function useMainScreenOnboardingMotion(window_height: number) {
  const reduce_motion = useReducedMotion();
  // Web: start intro complete so auth chips are not translated off-screen for a frame
  // (PWA reanimated stub has no frame-driven timing; native keeps the enter animation).
  const introProgress = useSharedValue(isWebRuntime() ? 1 : 0);
  const floatingY = useSharedValue(0);
  /** Window height on UI thread — avoids worklet deps on changing `window_height` from JS. */
  const window_height_sv = useSharedValue(window_height);

  const pLang = useSharedValue(0);
  const pLogo = useSharedValue(0);
  const pMenu = useSharedValue(0);
  const pJourney = useSharedValue(0);
  const pConsult = useSharedValue(0);
  const pPatt = useSharedValue(0);
  /** 0 = dock hidden, 1 = shown — drives floating auth + backdrop only (UI thread). */
  const dockOpen = useSharedValue(0);
  /** 0 = enter (scale) curve, 1 = exit (scaleOut) — pairs with `overlayPanelTransform`. */
  const dockExit = useSharedValue(0);
  /**
   * Login ↔ register crossfade in `MainScreenAuthForms` (0 = login, 1 = register).
   * Set together with `applyMainPhase` (no extra React on chip press).
   */
  const authFormSwap = useSharedValue(0);

  const duration = reduce_motion ? 1 : ONBOARD_TRANSITION_MS;
  const config = { duration, easing: ONBOARD_EASING };

  const applyMainPhase = useCallback(
    (target: 0 | 1 | 2) => {
      const cfg = { duration: reduce_motion ? 1 : ONBOARD_TRANSITION_MS, easing: ONBOARD_EASING };
      const forward = target > 0;
      pLang.value = withDelay(forward ? 0 : STAGGER * 4, withTiming(target, cfg));
      pLogo.value = withDelay(forward ? STAGGER : STAGGER * 3, withTiming(target, cfg));
      pMenu.value = withDelay(forward ? STAGGER * 2 : STAGGER * 2, withTiming(target, cfg));
      pJourney.value = withDelay(forward ? STAGGER * 3 : STAGGER, withTiming(target, cfg));
      pConsult.value = withDelay(forward ? STAGGER * 4 : 0, withTiming(target, cfg));
      pPatt.value = withTiming(target, cfg);
      authFormSwap.value = withTiming(target === 2 ? 1 : 0, cfg);
      const show_dock = target > 0;
      dockExit.value = show_dock ? 0 : 1;
      dockOpen.value = withTiming(show_dock ? 1 : 0, {
        duration: reduce_motion ? 1 : MAIN_SCREEN_DOCK_MS,
        easing: ONBOARD_EASING,
      });
    },
    [reduce_motion],
  );

  useEffect(() => {
    introProgress.value = withTiming(1, config);
  }, []);

  useLayoutEffect(() => {
    window_height_sv.value = window_height;
  }, [window_height, window_height_sv]);

  useEffect(() => {
    if (reduce_motion) return;
    floatingY.value = withRepeat(
      withSequence(
        withTiming(3, { duration: 28000, easing: Easing.inOut(Easing.sin) }),
        withTiming(-3, { duration: 28000, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      true,
    );
  }, [reduce_motion]);

  const card_enter_style = useAnimatedStyle(() => {
    'worklet';
    const wh = window_height_sv.value;
    const intro_ty = IS_ANDROID_MOTION ? 0 : interpolate(introProgress.value, [0, 1], [-wh, 0]);
    const introScale = IS_ANDROID_MOTION ? 1 : interpolate(pPatt.value, [0, 1, 2], [1, 1, 1]);
    const introOpacity = IS_ANDROID_MOTION ? 1 : interpolate(pPatt.value, [0, 1, 2], CARD_DOCK_OPACITY);
    return {
      transform: [
        { translateY: intro_ty },
        { scale: introScale },
      ],
      opacity: introOpacity,
    };
  });

  const bottom_enter_style = useAnimatedStyle(() => {
    'worklet';
    const wh = window_height_sv.value;
    const v90 = (wh * 90) / 100;
    const dock_lift = interpolate(pPatt.value, [0, 1, 2], [0, 10, 10]);
    return {
      transform: [
        {
          translateY: interpolate(introProgress.value, [0, 1], [v90, 0]) + dock_lift,
        },
      ],
      opacity: interpolate(pPatt.value, [0, 1, 2], DOCK_CHROME_OPACITY),
    };
  });

  const patt_animated_style = useAnimatedStyle(() => {
    'worklet';
    return {
      transform: [
        { translateY: floatingY.value },
        { scale: interpolate(pPatt.value, [0, 1, 2], [1, 1.06, 0.94]) },
      ],
    };
  });

  const blur_style = useBlurOverlayStyle(pMenu);
  const lang_style = useClusterRowStyle(pLang, window_height_sv, DOCK_LANG_OPACITY, [0, 0, 0], [1, 0.8, 0]);
  const logo_style = useClusterRowStyle(pLogo, window_height_sv, DOCK_CHROME_OPACITY, DOCK_LOGO_Y, DOCK_LOGO_SCALE);
  const menu_style = useClusterRowStyle(pMenu, window_height_sv, DOCK_CLUSTER_OPACITY, DOCK_CLUSTER_Y, [1, 0.7, 0.7]);
  const journey_style = useClusterRowStyle(pJourney, window_height_sv, DOCK_JOURNEY_OPACITY, DOCK_JOURNEY_Y, [1, 0.8, 0.8]);
  const consult_style = useClusterRowStyle(pConsult, window_height_sv, DOCK_CONSULT_OPACITY, DOCK_CONSULT_Y, [1, 0.9, 0.9]);

  const login_label_style = useChipPartStyle(pPatt, [1, 0, 1], [1, 0, 1]);
  const login_chevron_style = useChipPartStyle(pPatt, [0, 1, 0], [0, 1, 0]);
  const create_label_style = useChipPartStyle(pPatt, [1, 1, 0], [1, 1, 0]);
  const create_chevron_style = useChipPartStyle(pPatt, [0, 0, 1], [0, 0, 1]);

  return {
    applyMainPhase,
    authFormSwap,
    dockOpen,
    dockExit,
    card_enter_style,
    bottom_enter_style,
    patt_animated_style,
    lang_style,
    logo_style,
    menu_style,
    journey_style,
    consult_style,
    login_label_style,
    login_chevron_style,
    create_label_style,
    create_chevron_style,
    blur_style,
  };
}

/** Backdrop opacity for the inline auth float (Android renders this below the hero column). */
export function useMainScreenDockBackdropStyle(dockOpen: SharedValue<number>) {
  return useAnimatedStyle(() => ({ opacity: dockOpen.value }), [dockOpen]);
}

/** Backdrop + scaled panel transforms for the inline auth float (shared iOS/Android entry point). */
export function useMainScreenDockLayerMotion(
  window_height: number,
  window_width: number,
  colors: OverlayPanelThemeColors,
  dockOpen: SharedValue<number>,
  dockExit: SharedValue<number>,
) {
  const is_android = isMainScreenAndroid();
  const dock_panel_vh = window_height * MAIN_SCREEN_DOCK.panelVhFractionIos;

  const dock_backdrop_a = useAnimatedStyle(() => ({ opacity: dockOpen.value }), [dockOpen]);

  const dock_panel_a = useAnimatedStyle(() => {
    const panel = overlayPanelTransform(
      dockOpen.value,
      MAIN_SCREEN_DOCK_SLIDE_OFF,
      dockExit.value,
      'scale',
      'scaleOut',
      dock_panel_vh,
      colors,
    );
    if (is_android) {
      return stripAndroidMainScreenOverlayPanelFill(panel);
    }
    return panel;
  }, [colors, dock_panel_vh, dockOpen, dockExit]);

  return { dock_backdrop_a, dock_panel_a, dockOpen, dockExit };
}
